/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import numelDimension from '@stdlib/ndarray/base/numel-dimension';
import getStride from '@stdlib/ndarray/base/stride';
import getData from '@stdlib/ndarray/base/data-buffer';
import getOffset from '@stdlib/ndarray/base/offset';
import { ndarray as strided } from './../../../../strided/nanrange-by';


// MAIN //

/**
* Computes the range of a one-dimensional ndarray via a callback function, ignoring NaN values.
*
* ## Notes
*
* -   The function expects the following ndarrays:
*
*     -   a one-dimensional input ndarray.
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing ndarrays
* @param {Function} clbk - callback function
* @param {*} [thisArg] - callback execution context
* @returns {number} range
*
* @example
* import vector from '@stdlib/ndarray/vector/ctor';
* import nanrangeBy from '@stdlib/stats/base/ndarray/nanrange-by';
*
* var x = vector( [ 1.0, -2.0, NaN, 2.0 ], 'generic' );
*
* function clbk( value ) {
*     return value * 2.0;
* }
*
* var v = nanrangeBy( [ x ], clbk );
* // returns 8.0
*/
function nanrangeBy( arrays, clbk, thisArg ) {
	var x = arrays[ 0 ];
	return strided( numelDimension( x, 0 ), getData( x ), getStride( x, 0 ), getOffset( x ), wrapper, null ); // eslint-disable-line max-len

	/**
	* Invokes the provided callback.
	*
	* @private
	* @param {*} value - current array element
	* @param {NonNegativeInteger} aidx - current array element index
	* @param {NonNegativeInteger} sidx - current strided array element index
	* @param {Collection} arr - input array
	* @returns {*} result
	*/
	function wrapper( value, aidx ) {
		return clbk.call( thisArg, value, aidx, x );
	}
}


// EXPORTS //

export default nanrangeBy;
